import { JSX, Show, createEffect } from "solid-js";
import { useTelescopeContext } from "@hubble/telescope";
import { upiLogoLongList } from "~/assets/assets";
import { ExpiredState } from "./expired_state";
import { toRupees } from "~/utils/number";
import { goBack } from "~/shared_states/modal";
import { usePaymentLoaderContext } from "../context/payment_loader_context";

export function QrCodeView(): JSX.Element {
  const { components } = useTelescopeContext();
  const { amount, qrLink, isQrLoading, isExpired, minutes, seconds, timeLeft } =
    usePaymentLoaderContext();

  // Initialize QR code when link is available
  createEffect(() => {
    const link = qrLink();
    if (link && typeof window !== "undefined") {
      import("qr-code-styling").then((QRCode) => {
        const canvasElement = document.getElementById("qr-canvas");
        if (canvasElement) {
          canvasElement.innerHTML = ""; // Clear previous QR code
          const qrCode = new QRCode.default({
            width: 200,
            height: 200,
            data: link,
            margin: 0,
            qrOptions: {
              mode: "Byte",
              errorCorrectionLevel: "Q",
            },
            imageOptions: {
              hideBackgroundDots: true,
              imageSize: 0.4,
              margin: 0,
            },
            dotsOptions: { type: "dots", color: "#000000" },
            backgroundOptions: { color: "#ffffff" },
            image: "https://assets.myhubble.money/web-assets/buying_text.png",
            cornersSquareOptions: { type: "extra-rounded", color: "#000000" },
            cornersDotOptions: { color: "#000000" },
          });
          qrCode.append(canvasElement);
        }
      });
    }
  });

  const formatTime = (value: number) => (value < 10 ? `0${value}` : `${value}`);

  return (
    <div class="relative flex min-h-[70vh] flex-col overflow-hidden bg-background-normal-primary">
      {/* Header */}
      <components.ModalHeader
        size="large"
        title={toRupees(amount)}
        description="Scan & pay via any UPI app"
        onClose={() => goBack()}
      />

      {/* Content */}
      <div class="flex flex-col items-center gap-6 px-5 pb-6 pt-5">
        <Show when={!isExpired()} fallback={<ExpiredState />}>
          <div class="flex flex-col items-center gap-4">
            {/* QR Code container */}
            <div class="flex size-[202px] items-center justify-center rounded-xl border border-stroke-1">
              <Show
                when={!isQrLoading() && qrLink()}
                fallback={<components.Loader size="medium" />}
              >
                <div id="qr-canvas" class="size-[200px]" />
              </Show>
            </div>

            {/* UPI logos */}
            <div class="flex flex-col items-center gap-[6px] px-[11px]">
              <img
                src={upiLogoLongList}
                alt="UPI apps - GPay, PhonePe, Paytm, CRED, BHIM"
                class="h-[22px]"
              />
              <p class="text-center text-para-3-regular tracking-[-0.14px] text-text-normal-secondary">
                ... or any UPI app
              </p>
            </div>
          </div>

          {/* Timer */}
          <Show when={timeLeft() > 0}>
            <p class="text-center text-para-2-semi-bold tracking-[-0.16px] text-text-normal-primary">
              {formatTime(minutes())}:{formatTime(seconds())}
            </p>
          </Show>
        </Show>
      </div>

      {/* Footer */}
      <div class="absolute bottom-0 flex flex-col items-center gap-4 pb-6">
        <div class="h-px w-full bg-[#edeef1]" />
        <div class="px-6">
          <p class="text-center text-para-3-regular tracking-[-0.14px] text-text-normal-secondary">
            Note: You'll be redirected to another page once payment is
            processed.
          </p>
        </div>
      </div>
    </div>
  );
}
